import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import './CheckoutPage.css';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import UsersService from '../../common/Rest/Services/UserService/UsersService';
import {
  DeliveryDestination,
  DeliveryInformationData,
  DeliveryProvider,
  UserInfoData,
} from '../../common/Rest/Services/UserService/Data';
import orderService from '../../common/Rest/Services/OrderService/OrderService';
import ROUTES from '../../common/Routing/Routes';
import { getBookImageUrl } from '../../common/Utils/BookImages';
import { isMerchandiseItem } from '../../common/Utils/ItemLinks';
import DeliveryProviderIcon from '../../common/Delivery/DeliveryProviderIcon';
import NovaPoshtaAddressFields from '../../common/Delivery/NovaPoshtaAddressFields';
import SelfPickupFields from '../../common/Delivery/SelfPickupFields';
import {
  clearDeliveryTarget,
  clearProviderDetails,
  destinationLabel,
  emptyDelivery,
  getDeliveryDisplayName,
  providerLabel,
  renderProviderValue,
  toDeliveryPayload,
} from '../../common/Delivery/deliveryForm';

const formatPrice = (value: number) => `${Number(value || 0).toFixed(2)} грн`;

function CheckoutPage() {
  const { cartItems, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserInfoData | null>(null);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState<number | null>(null);
  const [deliveryForm, setDeliveryForm] = useState<DeliveryInformationData>(emptyDelivery());
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const user = await UsersService.getCurrentUser();
        setProfile(user);
        const primary = user.deliveryInformation?.find((item) => item.isPrimary) ?? user.deliveryInformation?.[0];
        setSelectedDeliveryId(primary?.id ?? null);
        setShowDeliveryForm(!primary);
        setDeliveryForm({
          ...emptyDelivery(),
          name: user.name || '',
          lastName: user.lastName || '',
          phone: user.phoneNumber || '',
          isPrimary: true,
        });
      } catch {
        setError('Не вдалося завантажити профіль. Спробуйте увійти ще раз.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const selectedDelivery = useMemo<DeliveryInformationData | undefined>(
    () => profile?.deliveryInformation?.find((item) => item.id === selectedDeliveryId),
    [profile, selectedDeliveryId]
  );

  const handleSubmit = async () => {
    setError(null);

    if (!cartItems.length) {
      setError('Список оренди порожній.');
      return;
    }

    if (!selectedDeliveryId) {
      setError('Додайте або оберіть адресу доставки.');
      return;
    }

    try {
      setSubmitting(true);
      const order = await orderService.createOrder({
        userComment: comment,
        userDeliveryInformationId: selectedDeliveryId,
        items: cartItems.map((item) => ({
          itemId: item.id,
          count: item.count || 1,
        })),
      });
      clearCart();
      setCreatedOrderId(order.id);
    } catch {
      setError('Не вдалося оформити оренду. Перевірте, чи книжки ще доступні.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeliverySubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSavingDelivery(true);
    setError(null);

    try {
      const saved = await UsersService.addDeliveryInformation({
        ...toDeliveryPayload(deliveryForm),
        isPrimary: true,
      });
      setProfile((current) => current ? {
        ...current,
        deliveryInformation: [
          ...(current.deliveryInformation ?? []).map((delivery) => ({ ...delivery, isPrimary: false })),
          saved,
        ],
      } : current);
      setSelectedDeliveryId(saved.id ?? null);
      setShowDeliveryForm(false);
    } catch {
      setError('Не вдалося зберегти адресу доставки. Перевірте поля.');
    } finally {
      setSavingDelivery(false);
    }
  };

  if (loading) {
    return (
      <div className="checkout-page default-container">
        <CircularProgress />
      </div>
    );
  }

  if (createdOrderId) {
    return (
      <div className="checkout-page default-container">
        <section className="checkout-success">
          <Typography variant="h4">Оренду оформлено</Typography>
          <Typography>Номер оренди: #{createdOrderId}</Typography>
          <Typography>
            Дякуємо за замовлення! Найближчим часом зв’яжемося з вами та надішлемо дані для оплати.
          </Typography>
          <Button variant="contained" onClick={() => navigate(ROUTES.HOME)}>
            Повернутися до книжок
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="checkout-page default-container">
      <div className="checkout-layout">
        <section className="checkout-main">
          <div className="checkout-panel">
            <Typography variant="h5">Оформлення оренди</Typography>
            <Typography color="text.secondary">
              Перевірте книжки і виберіть адресу доставки. Дані беруться з вашого профілю.
            </Typography>
          </div>

          <div className="checkout-panel">
            <Typography variant="h6">Доставка</Typography>
            {profile?.deliveryInformation?.length ? (
              <>
                <FormControl fullWidth>
                  <RadioGroup
                    value={selectedDeliveryId ?? ''}
                    onChange={(e) => setSelectedDeliveryId(Number(e.target.value))}
                  >
                    {profile.deliveryInformation.map((delivery) => (
                      <FormControlLabel
                        key={delivery.id}
                        value={delivery.id}
                        control={<Radio />}
                        label={
                          <span>
                            <strong>
                              {delivery.name} {delivery.lastName}
                            </strong>
                            {' · '}
                            {getDeliveryDisplayName(delivery)}
                            {delivery.isPrimary ? ' · основна' : ''}
                          </span>
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <Button variant="text" onClick={() => setShowDeliveryForm((value) => !value)}>
                  {showDeliveryForm ? 'Сховати форму' : 'Додати нову адресу'}
                </Button>
              </>
            ) : (
              <Alert severity="warning">
                Для оформлення оренди потрібна адреса доставки. Додайте її тут, і ми одразу використаємо її для замовлення.
              </Alert>
            )}

            {showDeliveryForm && (
              <form className="checkout-delivery-form" onSubmit={handleDeliverySubmit}>
                <div className="checkout-form-grid">
                  <TextField label="Ім'я" value={deliveryForm.name} onChange={(e) => setDeliveryForm({ ...deliveryForm, name: e.target.value })} required />
                  <TextField label="Прізвище" value={deliveryForm.lastName} onChange={(e) => setDeliveryForm({ ...deliveryForm, lastName: e.target.value })} required />
                  <TextField label="Телефон" value={deliveryForm.phone} onChange={(e) => setDeliveryForm({ ...deliveryForm, phone: e.target.value })} required />
                  <TextField
                    select
                    label="Служба доставки"
                    value={deliveryForm.provider}
                    SelectProps={{ renderValue: renderProviderValue }}
                    onChange={(e) => {
                      const provider = e.target.value as DeliveryProvider;
                      setDeliveryForm(clearProviderDetails({
                        ...deliveryForm,
                        provider,
                        destination: provider === DeliveryProvider.Self ? DeliveryDestination.Address : DeliveryDestination.Department,
                      }));
                    }}
                  >
                    {Object.entries(providerLabel).map(([value, label]) => (
                      <MenuItem key={value} value={value} className="provider-menu-item">
                        <DeliveryProviderIcon provider={value} />
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {deliveryForm.provider === DeliveryProvider.NovaPost ? (
                    <NovaPoshtaAddressFields
                      value={deliveryForm}
                      onChange={setDeliveryForm}
                      destinationControl={(
                        <TextField
                          select
                          label="Тип доставки"
                          value={deliveryForm.destination}
                          onChange={(e) => setDeliveryForm(clearDeliveryTarget(deliveryForm, e.target.value as DeliveryDestination))}
                        >
                          {Object.entries(destinationLabel).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  ) : deliveryForm.provider === DeliveryProvider.Self ? (
                    <SelfPickupFields value={deliveryForm} onChange={setDeliveryForm} />
                  ) : (
                    <>
                      <TextField
                        select
                        label="Тип доставки"
                        value={deliveryForm.destination}
                        onChange={(e) => setDeliveryForm(clearDeliveryTarget(deliveryForm, e.target.value as DeliveryDestination))}
                      >
                        {Object.entries(destinationLabel).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Місто"
                        value={deliveryForm.city}
                        onChange={(e) => {
                          const city = e.target.value;
                          setDeliveryForm({
                            ...deliveryForm,
                            city,
                            displayName: [city, deliveryForm.number].filter(Boolean).join(', '),
                          });
                        }}
                        required
                      />
                      <TextField
                        label="Номер відділення, індекс або адреса"
                        value={deliveryForm.number}
                        onChange={(e) => {
                          const number = e.target.value;
                          setDeliveryForm({
                            ...deliveryForm,
                            number,
                            displayName: [deliveryForm.city, number].filter(Boolean).join(', '),
                          });
                        }}
                        required
                      />
                    </>
                  )}
                </div>
                <Button type="submit" variant="contained" disabled={savingDelivery}>
                  {savingDelivery ? 'Зберігаємо...' : 'Зберегти адресу'}
                </Button>
              </form>
            )}

            {selectedDelivery && (
              <div className="selected-delivery">
                <div className="selected-delivery-title">
                  <DeliveryProviderIcon provider={selectedDelivery.provider} />
                  <strong>{getDeliveryDisplayName(selectedDelivery)}</strong>
                </div>
                <span>Телефон: {selectedDelivery.phone}</span>
              </div>
            )}
          </div>

          <div className="checkout-panel">
            <TextField
              label="Коментар до оренди"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </div>

          {error && <Alert severity="error">{error}</Alert>}
        </section>

        <aside className="checkout-summary">
          <Typography variant="h6">Ваш список оренди</Typography>
          <div className="checkout-items">
            {cartItems.length ? (
              cartItems.map((item) => (
                <div className="checkout-item" key={item.id}>
                  <img src={getBookImageUrl(item)} alt={item.name} />
                  <div>
                    <strong>{item.name}</strong>
                    <p>
                      {item.count || 1} x {formatPrice(item.price)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <Typography color="text.secondary">Список оренди порожній.</Typography>
            )}
          </div>
          <div className="checkout-total">
            <span>{cartItems.some(isMerchandiseItem) ? 'Разом' : 'Донат за оренду'}</span>
            <strong>{formatPrice(cartTotal)}</strong>
          </div>
          <Button
            type="submit"
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting || !cartItems.length || !selectedDeliveryId}
            fullWidth
          >
            {submitting ? 'Оформлюємо...' : 'Підтвердити оренду'}
          </Button>
        </aside>
      </div>
    </div>
  );
}

export default CheckoutPage;
